import { GoalFilterDocument, GoalType } from '@griply/types';
import { useGoalControls } from 'features/goals';
import React, { useMemo } from 'react';
import { DeleteGoalsProvider } from 'shared/contexts/delete-goals';
import {
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  UpdateGoal,
} from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { GoalGroupBy, GroupByMode } from 'shared/types/sorting-mode';

import { DeadlineGroupingGoalList } from './components/deadline-grouping-goal-list';
import { GoalGroupingGoalList } from './components/goal-grouping-goal-list';
import { LifeAreaGroupingGoalList } from './components/life-area-grouping-goal-list';
import { NoGroupingGoalList } from './components/no-grouping-goal-list';
import { ContextMenuProvider } from './context-menu-context';
import { useGoalsByFilterDocument } from './hooks/use-goals-by-filter-document';
import { ContextMenuOptions, GroupingGoalListProps } from './types';

const groupByLists: Record<GoalGroupBy, React.FC<GroupingGoalListProps>> = {
  [GroupByMode.Goal]: GoalGroupingGoalList,
  [GroupByMode.LifeArea]: LifeAreaGroupingGoalList,
  [GroupByMode.None]: NoGroupingGoalList,
  [GroupByMode.StartDate]: NoGroupingGoalList,
  [GroupByMode.Deadline]: DeadlineGroupingGoalList,
};

export type GoalFilterListProps = {
  filterDocument: GoalFilterDocument;
  customOrder?: ID[];
  // Per-parent child order from the page's order hook; without it children keep the list order.
  goalChildrenByParentId?: Map<ID, ID[]>;
  goalTypeFilter?: GoalType[];
  onSectionCollapse?: (collapsedSections: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  hideLifeArea?: boolean;
  hideArchived?: boolean;
  hideCompleted?: boolean;
  rootLink?: { goalId?: ID; lifeAreaId?: ID };
  showProjectParent?: boolean;
  // Suppress the life-area overline on a project card even when
  // `showProjectParent` is on — used by lists bound to one life area where the life
  // area is already the page context (only the parent-goal overline is useful).
  hideProjectLifeAreaLabel?: boolean;
  // Deadline grouping only: off lists every goal in its own quarter, with its parents as the overline.
  nestSubGoals?: boolean;
  // Goal grouping only: sections are root goals, sub-goals nest as folders down to their projects.
  nestGoals?: boolean;
  onReorder?: (
    updatedSections: { id: ID; goalSorting: ID[] }[],
    updatedGoals: UpdateGoal[],
    goalLinkChanges?: GoalLifeAreaLinkChange[],
    goalParentChanges?: GoalParentLinkChange[],
  ) => void;
  isMultiSelectModifierPressed?: boolean;
  excludedContextMenuOptions?: ContextMenuOptions[];
};

const GoalFilterListContent: React.FC<GoalFilterListProps> = ({
  filterDocument,
  customOrder,
  goalChildrenByParentId,
  goalTypeFilter,
  scrollElement,
  hideLifeArea,
  hideArchived,
  hideCompleted,
  rootLink,
  showProjectParent,
  hideProjectLifeAreaLabel,
  nestSubGoals = true,
  nestGoals,
  onSectionCollapse,
  onReorder,
  isMultiSelectModifierPressed,
  excludedContextMenuOptions,
}) => {
  // fetch the needed data
  const goals = useGoalsByFilterDocument(filterDocument, {
    customOrder,
    goalTypeFilter,
  });

  // Connect controls if not provided
  const { onUpdateGoalProgress } = useGoalControls(goals);

  // GroupBy
  const groupMode = filterDocument?.groupByMode ?? GroupByMode.LifeArea;
  const GroupedList = groupByLists[groupMode] ?? groupByLists[GroupByMode.None];

  // The parent overline is only useful where the parent isn't shown — projects in a
  // goals list, and every row of a flattened list — and is redundant when already grouped by it.
  const showParentLabelsForGoals = !nestSubGoals;
  const showParentLabels = !!showProjectParent || showParentLabelsForGoals;
  const showParentGoalLabel =
    showParentLabels && groupMode !== GroupByMode.Goal;
  const showParentLifeAreaLabel =
    showParentLabels &&
    !hideProjectLifeAreaLabel &&
    groupMode !== GroupByMode.LifeArea;

  // A projects-only list: section-header "+" creates projects, not goals.
  const isProjectList =
    !!goalTypeFilter?.length &&
    goalTypeFilter.every((type) => type === GoalType.Project);

  // Collapsed State
  const collapsedSections = useMemo(
    () => filterDocument?.collapsedSections ?? [],
    [filterDocument?.collapsedSections],
  );

  return (
    <GroupedList
      goals={goals}
      goalChildrenByParentId={goalChildrenByParentId}
      collapsedGoals={collapsedSections}
      onGoalCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      onReorder={onReorder}
      onUpdateProgress={onUpdateGoalProgress}
      hideLifeArea={hideLifeArea}
      hideArchived={hideArchived}
      hideCompleted={hideCompleted}
      rootLink={rootLink}
      showParentGoalLabel={showParentGoalLabel}
      showParentLifeAreaLabel={showParentLifeAreaLabel}
      showParentLabelsForGoals={showParentLabelsForGoals}
      nestSubGoals={nestSubGoals}
      nestGoals={nestGoals}
      isProjectList={isProjectList}
      isMultiSelectModifierPressed={isMultiSelectModifierPressed}
      excludedContextMenuOptions={excludedContextMenuOptions}
    />
  );
};

export const GoalFilterList: React.FC<GoalFilterListProps> = (props) => (
  <ContextMenuProvider>
    <DeleteGoalsProvider>
      <GoalFilterListContent {...props} />
    </DeleteGoalsProvider>
  </ContextMenuProvider>
);
